import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import PageHeader from '@/components/ui/page-header';
import { ResponsiveContainer } from './ResponsiveContainer';
import AdminJsonToggle from './AdminJsonToggle';
import type { EnhancedSurveyQuestion } from '@/types/api/enhanced-survey';
import type { Module } from '@/types/api';

interface DynamicSurveyQuestionProps {
  questionNumber: number;
  totalQuestions: number;
  question: EnhancedSurveyQuestion;
  onAnswer: (answer: number | string) => void;
  currentAnswer?: number | string;
  onBack: () => void;
  onNext: () => void;
  canGoBack: boolean;
  canGoNext: boolean;
  surveyName: string;
  module?: Module;
}

/**
 * DynamicSurveyQuestion renders survey questions based on backend data
 * Replaces UniversalSurveyQuestion for Phase 2 data-driven architecture
 */
const DynamicSurveyQuestion = ({
  questionNumber,
  totalQuestions,
  question,
  onAnswer,
  currentAnswer,
  onBack,
  onNext,
  canGoBack,
  canGoNext,
  surveyName,
  module
}: DynamicSurveyQuestionProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | string | undefined>();
  const [textValue, setTextValue] = useState<string>('');

  useEffect(() => {
    if (currentAnswer !== undefined) {
      setSelectedAnswer(currentAnswer);
      if (typeof currentAnswer === 'string') {
        setTextValue(currentAnswer);
      }
    } else {
      setSelectedAnswer(undefined);
      setTextValue('');
    }
  }, [currentAnswer, questionNumber]);

  // Remove debounced text update - text responses will be submitted on Next/Finish click only

  const handleAnswerSelect = (value: number | string) => {
    console.log('DynamicSurveyQuestion: handleAnswerSelect called with:', value);
    if (typeof value === 'string') {
      // For text responses, just update local state - submission happens on Next/Finish click
      setTextValue(value);
      setSelectedAnswer(value);
    } else {
      // For scale responses, update state and submit immediately
      setSelectedAnswer(value);
      onAnswer(value);
    }
  };

  if (!question) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Question not found</div>
      </div>
    );
  }

  const progress = Math.round((questionNumber / totalQuestions) * 100);
  const isTextQuestion = question.responseType === 'text';
  
  // Check if the question has been answered
  const hasAnswer = isTextQuestion 
    ? (textValue?.trim().length ?? 0) > 0 
    : selectedAnswer !== undefined;

  // Validate response type
  if (question.responseType !== 'scale' && question.responseType !== 'text') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-500">Unsupported question type: {question.responseType}</div>
      </div>
    );
  }

  // Use scale configuration from question or fall back to default
  const scaleRange = [1, 2, 3, 4, 5];
  
  // Get scale labels from question configuration or use default
  const getScaleLabel = (value: number): string => {
    // Check if question has scale configuration with labels
    if (question.scaleMinLabel && value === 1) return question.scaleMinLabel;
    if (question.scaleMaxLabel && value === 5) return question.scaleMaxLabel;
    
    // Default labels if not provided by backend
    const defaultLabels = ['Disagree', 'Somewhat disagree', 'Neutral', 'Somewhat agree', 'Agree'];
    return defaultLabels[value - 1] || value.toString();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AdminJsonToggle 
        data={{
          question,
          questionNumber,
          totalQuestions,
          currentAnswer,
          selectedAnswer,
          surveyName,
          module
        }}
        label="Survey Question Data"
      />
      <PageHeader 
        title={module?.uiTitle || ''}
        variant="progress"
        progress={progress}
        progressSubtext={`Question ${questionNumber} of ${totalQuestions}`}
      />

      {/* Question Content */}
      <ResponsiveContainer maxWidth="2xl" className="flex-1 py-6 flex flex-col">
        <div className="flex-1 bg-white lg:bg-gray-50 lg:rounded-lg lg:p-8 lg:shadow-sm">
          <h2 className="text-xl lg:text-2xl font-semibold text-black mb-6 leading-relaxed">
            {question.text}
          </h2>

          {isTextQuestion ? (
            /* Text input for open-ended question */
            <div className="lg:max-w-3xl">
              <Textarea
                value={textValue}
                onChange={(e) => handleAnswerSelect(e.target.value)}
                placeholder={question.textPlaceholder || 'Please share your thoughts...'}
                className="w-full min-h-[200px] lg:min-h-[250px] p-4 lg:p-5 border-2 border-[#cccccc] rounded-lg resize-none focus:border-black focus:ring-2 focus:ring-black/20 text-base lg:text-lg transition-all"
                minLength={question.textMinLength}
                maxLength={question.textMaxLength}
              />
              <div className="mt-2 text-sm lg:text-base text-gray-600">
                {question.textMaxLength 
                  ? `${textValue.length} / ${question.textMaxLength}`
                  : `${textValue.length} characters`
                }
              </div>
            </div>
          ) : (
            /* Likert Scale for scale questions */
            <div className="space-y-3 lg:space-y-4 lg:max-w-2xl">
              {scaleRange.map((value) => {
                const isSelected = selectedAnswer === value;
                const label = getScaleLabel(value);
                
                return (
                  <button
                    key={value}
                    onClick={() => handleAnswerSelect(value)}
                    className={`w-full p-4 lg:p-5 rounded-lg border-2 text-left transition-all transform hover:scale-[1.02] ${
                      isSelected 
                        ? 'border-black bg-black text-white shadow-md' 
                        : 'border-[#cccccc] bg-white text-black hover:border-[#666666] hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-white bg-white' : 'border-[#cccccc]'
                      }`}>
                        {isSelected && (
                          <div className="w-3 h-3 rounded-full bg-black"></div>
                        )}
                      </div>
                      <span className="font-medium text-base lg:text-lg">{label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

        </div>

        {/* Navigation */}
        <div className="space-y-4 mt-6 lg:mt-8">
          <div className="flex justify-between gap-4 lg:max-w-lg lg:mx-auto">
            <Button
              onClick={onBack}
              disabled={!canGoBack}
              variant="outline"
              className="flex-1 h-12 lg:h-14 border-[#cccccc] text-black hover:bg-[#f7f7f7] transition-all text-base lg:text-lg"
            >
              <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
              Previous
            </Button>
            
            <Button
              onClick={() => {
                // Submit text response before moving to next question
                if (question.responseType === 'text' && textValue && textValue !== currentAnswer) {
                  onAnswer(textValue);
                }
                onNext();
              }}
              disabled={!canGoNext || !hasAnswer}
              className="flex-1 h-12 lg:h-14 bg-black text-white hover:bg-[#666666] transition-all text-base lg:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {questionNumber === totalQuestions ? 'Finish' : 'Next'}
              <ArrowRight className="h-4 w-4 lg:h-5 lg:w-5 ml-2" />
            </Button>
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default DynamicSurveyQuestion;